import React, { useCallback, useEffect, useState } from 'react';
import {
  generateToolStub, getToolCatalog,
  type ToolCatalog, type ToolCatalogGap, type ToolCatalogRow,
} from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';
import AwarenessStrip from './AwarenessStrip';
import CoverageGapsPanel from './CoverageGapsPanel';
import DomainList from './DomainList';
import DriftPanel from './DriftPanel';
import EntityCoveragePanel from './EntityCoveragePanel';
import ModuleCoverageMatrix from './ModuleCoverageMatrix';
import ToolEditorModal from './ToolEditorModal';
import { RubyLoader } from '../../../components/RubyLoader';

const CoverageTab: React.FC<AiTabProps> = ({ onHeaderStats, showToast }) => {
  // Every hook sits ABOVE every early return. The previous version declared
  // one after `if (!catalog) return`, so the hook count changed once the
  // catalog loaded and React threw "Rendered more hooks than during the
  // previous render", crashing this tab.
  const [catalog, setCatalog] = useState<ToolCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissedGaps, setDismissedGaps] = useState<Set<string>>(new Set());
  const [stubbing, setStubbing] = useState<string | null>(null);
  const [stubError, setStubError] = useState<string | null>(null);
  const [editingTool, setEditingTool] = useState<ToolCatalogRow | null>(null);

  const refetch = useCallback(async () => {
    try {
      const cat = await getToolCatalog();
      setCatalog(cat);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load the tool catalog');
    }
  }, []);

  useEffect(() => { void refetch(); }, [refetch]);

  useEffect(() => {
    if (!catalog) return;
    onHeaderStats([
      { label: 'Tools', value: String(catalog.totals.total), tone: 'white' },
      { label: 'Full', value: String(catalog.totals.full), tone: 'emerald' },
      { label: 'Gaps', value: String(catalog.inventory?.totalGaps ?? 0), tone: 'rose' },
    ]);
  }, [catalog, onHeaderStats]);

  const handleGenerateStub = async (gap: ToolCatalogGap) => {
    setStubError(null);
    setStubbing(gap.sk);
    try {
      await generateToolStub({ inventorySk: gap.sk });
      setDismissedGaps((prev) => new Set(prev).add(gap.sk));
      showToast(`Stub created for ${gap.method} ${gap.path}`, 'success');
    } catch (err: any) {
      setStubError(err?.message || 'Failed to generate stub');
    } finally {
      setStubbing(null);
    }
  };

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm">
        Failed to load Agent Coverage: {error}
      </div>
    );
  }
  if (!catalog) {
    return (
      <RubyLoader variant="page" className="bg-white border border-stone-200 rounded-xl" />
    );
  }

  const visibleGaps = (catalog.gaps || []).filter((g) => !dismissedGaps.has(g.sk));

  return (
    <div className="space-y-5">
      {catalog.inventory && (
        <AwarenessStrip
          inventory={catalog.inventory}
          coverageTotals={catalog.totals}
          modules={catalog.modules || []}
        />
      )}

      {catalog.inventory && (
        <CoverageGapsPanel
          gaps={visibleGaps}
          stubbing={stubbing}
          stubError={stubError}
          onGenerateStub={handleGenerateStub}
          onDismissError={() => setStubError(null)}
        />
      )}

      {catalog.drift && catalog.drift.length > 0 && (
        <DriftPanel
          drift={catalog.drift}
          onEdit={(toolId) => {
            const tool = catalog.domains.flatMap((d) => d.tools).find((t) => t.id === toolId);
            if (tool) setEditingTool(tool);
          }}
        />
      )}

      {catalog.modules && catalog.modules.length > 0 && (
        <ModuleCoverageMatrix modules={catalog.modules} onRefetch={refetch} />
      )}

      {catalog.entities && catalog.entities.length > 0 && (
        <EntityCoveragePanel entities={catalog.entities} />
      )}

      <DomainList
        domains={catalog.domains}
        totals={catalog.totals}
        onEditTool={setEditingTool}
      />

      {editingTool && (
        <ToolEditorModal
          tool={editingTool}
          onClose={() => setEditingTool(null)}
          onSaved={async () => { setEditingTool(null); await refetch(); }}
        />
      )}
    </div>
  );
};

export default CoverageTab;
